/* eslint-disable max-len */
export const C_APP = Object.freeze({
    CLIENT_URLS: [
        /\/(contents|settings|landing|legal|cme)\/.*/,
        /\/content-not-avaliable-icg/,
        /\/login$/,
        /\/login-register/,
        /\/account\/reset-password/,
        /\/account\/change-password/,
        /\/account\/consolidate-accounts/,
        /\/account\/error/,
        /\/guid-error/,
        /\/validate-code/,
        /\/server-test/,
        /\/cme/,
        /\/view-error/,
        /\/content-not-available/,
        /\/page-not-found/,
        /\/myuptodatemobile/,
        /^\/pathway\/\d+\/contributor.*/,
        /^\/pathway\/\d+\/formulary-info.*/,
        /^\/rxtransitions\/contributor.*/
    ],
    /* eslint-disable no-useless-escape */
    DESTINATION_URLS: [
        /^\/contents\/.*?([^\/]+)$/,
        /^\/contents\/search\?/,
        /^\/drug-interactions\/\?/,
        /^\/contents\/grade\/([^\/]+)$/,
        /^\/account\/cme\/([^\/]+)$/,
        /^\/account\/cme\/moc\/([^\/]+)$/,
        /^\/pathway\/\d+\/contributor.*/,
        /^\/rxtransitions\/contributor.*/,
        /\/idp\/auth/
    ],
    /* eslint-enable no-useless-escape */
    META_DESCRIPTION: 'UpToDate, electronic clinical resource tool for physicians and patients that provides information on Adult Primary Care and Internal Medicine, Allergy and Immunology, Cardiovascular Medicine, Emergency Medicine, Endocrinology and Diabetes, Family Medicine, Gastroenterology and Hepatology, Hematology, Infectious Diseases, Nephrology and Hypertension, Neurology, Obstetrics, Gynecology, and Women\'s Health, Oncology, Pediatrics, Pulmonary, Critical Care, Sleep Medicine, Rheumatology, Surgery, and more.',
    PRODUCT_NAMES: {
        UTD: 'UpToDate<sup>&reg;</sup>',
        UCC: 'UpToDate \u4E34\u5E8A\u987E\u95EE'
    },
    COUNTRY_CODES: {
        UNITED_STATES: 'US',
        CHINA: 'CN'
    },
    LOGO_FILENAMES: {
        UTD: 'utd-logo',
        UCC: 'utd-logo-cc'
    },
    LOG_NAME: 'UTDWEBCLIENT',
    Z_INDEX: {
        MAX_ZINDEX: 999999999
    }
});

export const TIME_MS = Object.freeze({
    SECOND_MS: 1000,
    MINUTE_MS: 1000 * 60,
    HOUR_MS: 1000 * 60 * 60
});

export const DATA_UNITS_BYTES = Object.freeze({
    KiB: 1024,
    MiB: 1024 * 1024
});

export const C_AUTOCOMPLETE = Object.freeze({
    ITEM_TYPES: {
        HEADER: {
            DEFAULT: 'header',
            ROUTED_DRUG: 'routedDrugHeader'
        },
        CLICKABLE: {
            DEFAULT: 'link',
            PERSONALIZED: 'personalized'
        }
    },
    GUIDED_COMPLETIONS: {
        PARENT_CLASS: 'ac-guided-completion-suggestion-parent',
        CHILD_CLASS: 'ac-guided-completion-suggestion-sub'
    },
    AC_ITEM_SIZE: {
        MAX: 10,
        MIN: 0
    }
});

export const C_AI_SEARCH = Object.freeze({
    RESIZE_OVERFLOW_TOLERANCE_PX: 4,
    MAX_TIMEOUT_MS: 6000,
    DEADLINE_MAX_MS: 500,
    AI_COMPONENT_TEXT: {
        HEADER: 'AI Suggested Results',
        SUB_HEADER: 'These passages are excerpts from UpToDate. They are not generated by AI. Click \'Read More\' below to view the passage within the source content.',
        FEEDBACK: 'Are suggested results helpful?'
    },
    AI_CACHE: {
        CACHE_NAME: 'prevAiResultsCache',
        AI_CACHE_MAX_BYTES: DATA_UNITS_BYTES.MiB * 2,
        AI_CACHE_ITEM_EXPIRATION_MS: TIME_MS.HOUR_MS,
        AI_CACHE_CUR_VERSION: 1.1
    },
    ERROR_TYPES: {
        QUERY_REJECTION: 'Status 1000',
        NO_RESULTS: 'No results for query',
        LANGUAGE_NOT_SUPPORTED: 'User language is not supported',
        FILTERED_RESULTS: 'Results Filtered Out'
    },
    ERROR_TEXT: {
        GENERIC: [
            'No relevant passages retrieved',
            'Please use search results below or rephrase your question and try again.'
        ],
        TIMEOUT: [
            'AI Suggested Results Beta passage retrieval is still in progress...'
        ]
    },
    EVENTS: {
        TOPIC_SOURCE: 'NLP_SEARCH',
        TOPIC_SOURCE_RM: 'NLP_SEARCH_READMORE',
        TOPIC_SOURCE_BREADCRUMBS: 'NLP_SEARCH_BREADCRUMBS',
        USAGE_TYPE: 'NLP'
    },
    QUALTRICS: {
        SNIPPET_MAX_BEFORE_TRUNC: 40,
        SNIPPET_TRUNC_CUTOFF: 20, // Num of characters included from original snippet if truncated
        ELLIPSIS_LENGTH: 3 // Length of ellipsis string
    }
});

export const C_HTTP = Object.freeze({
    REST_BASE_PATH: '/services/app/',
    DEFAULT_HOMEPAGE_PATH: '/contents/search',
    DEFAULT_LOGIN: '/login',
    DEFAULT_TIMEOUT: 35000,
    WARNING_AFTER: 25000
});

export const C_HTTP_STATUS = Object.freeze({
    OK: 200,
    NO_CONTENT: 204,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    SERVER_ERROR: 500,
    SERVICE_UNAVAILABLE: 503
});

export const C_REST_URL = Object.freeze({
    GRAPHIC_IMAGE_REST_PREFIX_URL: `${C_HTTP.REST_BASE_PATH}contents/graphic/view/`
});

export const C_I18N = Object.freeze({
    UTD_NAMESPACE: 'utd'
});

export const C_EXPIRE_MESSAGE = Object.freeze({
    EXPIRED: 'EXPIRED',
    EXPIRING: 'EXPIRING',
    EXPIRES_TODAY: 'EXPIRES_TODAY'
});

export const C_KEYCODES = Object.freeze({
    ESCAPE_KEYCODE: 27,
    SPACE_KEY: 32,
    ENTER_KEY: 13,
    LEFT_ARROW: 37,
    RIGHT_ARROW: 39,
    DOWN_ARROW: 40,
    UP_ARROW: 38,
    HOME_KEYCODE: 36,
    END_KEYCODE: 35,
    TAB_KEY: 9
});

export const C_UI = Object.freeze({
    MENU_HIDE_DELAY_MS: 500,
    MENU_SHOW_DELAY_MS: 500,
    RESIZE_DELAY_MS: 150,
    RESIZE_DELAY_FAST_MS: 10,
    SCROLLING_CHECK_DELAY_LONG_MS: 250,
    SCROLLING_CHECK_DELAY_FAST_MS: 10,
    SAFARI_SCROLL_RESET_DELAY_MS: 100,
    DEFAULT_SPINNER_DELAY_MS: 2000,
    WINDOW_RESIZE_DELAY_MS: 50,
    MODAL_CLOSING_DELAY_MS: 500,
    DESKTOP_OVERFLOW_DELAY_MS: 25,
    VIEWPORT_GUTTER_PX: 8,
    CLICK_EVENT_DELAY_MS: 2000,
    FIXED_TOOLBAR_POSITION_PX: 40,
    HISTORY_ITEMS_PER_PAGE: 20,
    SMALL_MOBILE_BREAKPOINT: 450,
    MOBILE_BREAKPOINT: 768,
    RX_TRANSITIONS_MENU_BREAKPOINT: 950,
    DELAYED_REFRESH_TIMEOUT: 3000,
    AUTOCOMPLETE_DROPDOWN_DELAY: 350,
    CAPTCHA_RENDER_DELAY_MS: 1000,
    DOM_STABLE_IDLE_DELAY_MS: 200
});

export const C_BROWSER = Object.freeze({
    TYPE_SMALL_SCREEN: 'isSmallScreen',
    TYPE_TABLET: 'isTablet',
    TYPE_DESKTOP: 'isDesktop',
    OS_APPLE_MOBILE: 'ios',
    OS_APPLE_DESKTOP: 'macos',
    NAME_MICROSOFT_IE: 'InternetExplorer',
    NAME_MICROSOFT_EDGE: 'Edge',
    NAME_APPLE_SAFARI: 'Safari',
    NAME_CHROME_FOR_IOS: 'ChromeforiOS',
    NAME_FIREFOX_FOR_MOBILE: 'FirefoxforMobile',
    NAME_ANDROID_CHROME: 'AndroidChrome',
    NAME_MOBILE_SAFARI: 'MobileSafari',
    VENDOR_APPLE: 'Apple'
});

export const C_RESPONSIVE = Object.freeze({
    IS_NOT_DESKTOP: '',
    MOBILE_VIEW: 'mobile',
    DESKTOP_VIEW: 'desktop',
    MIN_DESKTOP_THRESHOLD_PX: 768,
    MAX_TABLET_THRESHOLD_PX: 1024
});

export const C_DOMAINS = Object.freeze({
    OPTIMIZELY_CDN: 'https://rollouts-cdn.uptodate.com',
    QUALTRICS_CDN: 'https://research.uptodate.com'
});

export const C_PATHS = Object.freeze({
    TOC_PREFIX: 'table-of-contents/',
    FOOTER_REST_EP: 'config/footer/json'
});

export const C_CME = Object.freeze({
    HISTORY_ITEMS_PER_PAGE: 20
});

export const C_CME_UI_LINKS = Object.freeze({
    dashboard: '/cme/dashboard',
    redeem: '/cme/redeem',
    history: '/cme/history',
    settings: '/cme/settings',
    help: '/cme/help',
    accreditation: '/home/cmececpd-accreditations',
    error: '/cme/error',
    confirmation: '/cme/confirmation',
    register: '/cme/register',
    redeemEvaluate: '/cme/redeem/evaluate',
    knowledgeCenter: 'https://wkhealthce.my.site.com/customers/s/'
});

export const C_CME_LABELS = Object.freeze({
    MOC_TEXT: '<span class="bold-text">MOC</span> - MAINTENANCE OF CERTIFICATION',
    SMB_TEXT: '<span class="bold-text">SMB</span> - STATE MEDICAL BOARDS'
});

export const C_MOC_UI_LINKS = Object.freeze({
    dashboard: '/cme/moc/dashboard',
    submit: '/cme/moc/submit',
    status: '/cme/moc/status',
    settings: '/cme/settings/moc',
    help: `${C_CME_UI_LINKS.knowledgeCenter}article/What-is-my-MOC-CC-board-ID-for-CME-e-submissions?language=en_US`,
    deadlineHelp: `${C_CME_UI_LINKS.knowledgeCenter}article/When-is-the-deadline-for-submitting-CME-credits-to-the-boards-participating-in-direct-credit-reporting?language=en_US`
});

export const C_MOC = Object.freeze({
    HEADER_COLUMNS: [
        'Credits',
        'Search Term',
        'Topic(s) Reviewed',
        'Search Goal',
        'Application',
        'Searched',
        'CME Redeemed'
    ],
    MOC_TYPE_STATE_CME: 'stateCme',
    MOC_TYPE_MOC: 'moc',
    MOC_SETTINGS_KEY_STATE_CME: 'stateCme',
    MOC_SETTINGS_KEY_MOC: 'moc',
    MOC_AUTHORITY_TYPE_STATE_CME: 'STATE'
});

export const C_STATE_CME_UI_LINKS = Object.freeze({
    dashboard: '/cme/state/dashboard',
    submit: '/cme/state/submit',
    status: '/cme/state/status',
    settings: '/cme/settings/state',
    help: `${C_CME_UI_LINKS.knowledgeCenter}article/What-is-my-state-medical-board-ID-for-CME-e-submissions?language=en_US`
});

export const C_TOPIC = Object.freeze({
    VIEW_TOPIC_KEY: 'CONTENT.VIEW_TOPIC',
    VIEW_OUTLINE_KEY: 'CONTENT.VIEW_OUTLINE',
    ANCHOR_HASH_PREFIX: 'xhash_',
    SCROLL_TARGET_SUFFIX: '_scrollTarget',
    SCROLL_TOP_POSITION: -100,
    TOOLBAR_SCROLL_OFFSET_PX: 16,
    TOPIC_CONTENT_STABLE_DELAY_MS: 500,
    OPTIMIZELY_TIMEOUT_MS: 1000,
    OPTIMIZELY_TIMEDOUT_LOG_PERCENT_CHANCE: 5,
    OPTIMIZELY_TIMEDOUT_EVENT_NAME: 'optimizely_timedout',
    FIT_HEIGHT: 98,
    FIT_HEIGHT_HOMEPAGE: 52,
    TOOLS_MENU_BTN_SELECTOR: '#toolsMenu',
    CALCULATOR_SCRIPT_TEMPLATE: calcJs => `//@ sourceURL=calculatorScript\n${calcJs}`,
    CALCULATOR_INIT_JAVASCRIPT: '//@ sourceURL=initCalc\nif (typeof initCalc == \'function\'){ initCalc(); }\nif (typeof initDropdowns == \'function\'){ initDropdowns(); }',
    TOPIC_TAB_HASH: 'topicContent',
    GRAPHIC_TAB_HASH: 'topicGraphics',
    TOPIC_TAB_INDEX: 0,
    GRAPHIC_TAB_INDEX: 1,
    SOURCE: {
        TOPIC_TABS: 'topic_tabs',
        GRAPHICS_GALLERY: 'graphics_gallery'
    },
    TYPES: {
        DRUG: 'drug',
        MEDICAL: 'medical'
    },
    SUBTYPES: {
        GENERAL_MEDICAL: 'medical_review',
        BASICS: 'medical_basics',
        DXY_DRUG: 'drug_dxy',
        DXY_PACKAGE_INSERT: 'drug_dxy_package_insert',
        LAB_INTERPRETATION: 'narrative_labi',
        DRUG_GENERAL: 'drug_general',
        DRUG_PEDIATRIC: 'drug_pediatric',
        DRUG_INTERNATIONAL: 'drug_international'
    },
    TOPIC_NOT_AVAILABLE: 'Topic Not Available'
});

export const C_SEARCH = Object.freeze({
    SUGGESTION_TYPE: {
        MATCHED: 'MATCHED',
        REPLACEMENT: 'REPLACEMENT',
        RELAUNCH: 'RELAUNCH',
        SEMANTIC: 'SEMANTIC',
        MISSPELL: 'MISSPELL',
        AUTOCORRECT: 'AUTOCORRECT'
    },
    SEARCH_TYPES: {
        PLAIN_TEXT: 'PLAIN_TEXT',
        GRAPHICS: 'GRAPHICS'
    },
    SOURCE: {
        USER_INPUT: 'USER_INPUT',
        HL7: 'HL7',
        USER_PREF: 'USER_PREF',
        PAGING: 'PAGING',
        COLLAPSE_VIEW: 'COLLAPSE_VIEW',
        GRAPHICS_PANEL: 'Graphic_Panel',
        GRAPHICAL_ANSWERS: 'Graphic_Answer'
    },
    CONTROL: {
        TOP_PULLDOWN: 'TOP_PULLDOWN'
    },
    PRIORITY: {
        ALL_TOPICS: '0',
        ADULT: '1',
        PEDIATRIC: '2',
        PATIENT: '3',
        GRAPHICS: '4'
    },
    TYPE_TITLES: {
        ICG: 'UpToDate Pathways: ',
        NARRATIVE: 'Lab Interpretation: '
    },
    WARNING_TEXT: {
        BRAND_NAMES_DIFFER_BY_COUNTRY: 'Brand name matches different drugs depending on country',
        SEARCH_MATCHES_MULTIPLE_DRUGS: 'Search term matches more than one drug'
    },
    INVALID_TAB_IDX: -1,
    RETRIGGER_TAB_WATCH_IDX: -2,
    HOMEPAGE_COPYRIGHT: {
        DEFAULT: '<strong>UpToDate content is protected by copyright </strong>and owned and/or licensed by UpToDate, Inc.  By accessing or using UpToDate content, you accept the terms and conditions of the <a href="https://www.wolterskluwer.com/en/know/clinical-effectiveness-terms" target="_blank" track="enabled">CE Terms of Use</a>, which prohibit the use, training, inputting or processing of UpToDate content by or into automated software or tools, including, but not limited to, artificial intelligence solutions, algorithms, machine learning, and/or large language models.',
        UCC: 'UpToDate 内容受版权保护，由 UpToDate, Inc. 所有和/或许可。访问或使用 UpToDate 内容，即表示您接<a href="https://www.uptodate.cn/legal/china/license" target="_blank">受临床效能部门的使用条款</a>及使用条件，其中禁止通过自动化软件或工具（包括但不限于人工智能解决方案、算法、机器学习和/或大型语言模型）使用、训练、输入或处理UpToDate 内容。'
    }
});

export const C_MYUTD_TABS = Object.freeze({
    HISTORY: 0,
    MOST_VIEWED: 1,
    BOOKMARKS: 2
});

export const UCC_FQDN_PATH = 'http://www.uptodate.cn/home';
export const WK_UPTODATE_PREFIX = 'https://www.wolterskluwer.com/en/solutions/uptodate';

export const C_HEADER = Object.freeze({
    URLS: {
        WHY_UPTODATE: `${WK_UPTODATE_PREFIX}/clinical-decision-support`,
        PRODUCT: WK_UPTODATE_PREFIX,
        EXPERT_AI: `${WK_UPTODATE_PREFIX}/ai-clinical-decision-support`,
        EDITORIAL: `${WK_UPTODATE_PREFIX}/about/editorial-process`,
        SUBOPTS: `${WK_UPTODATE_PREFIX}/subscribe`,
        PRIVACY_POLICY: '/home/privacy-policy',
        CN_PRIVACY_POLICY: 'http://www.uptodate.cn/home/uptodate-china-privacy-policy'
    },
    UCC_URLS: {
        WHY_UPTODATE: `${UCC_FQDN_PATH}/customer-testimonials`,
        PRODUCT: `${UCC_FQDN_PATH}/product`,
        EDITORIAL: `${UCC_FQDN_PATH}/editorial`,
        SUBOPTS: `${UCC_FQDN_PATH}/subscribe-options`
    }
});

export const BCDR_MESSAGE = Object.freeze({
    DEFAULT: 'This feature is currently unavailable due to scheduled maintenance. Please try again later.',
    APOLOGY: 'We apologize for the inconvenience and appreciate your patience. Thank you for using UpToDate.',
    TITLE_TEXT_FN: (featureName) => featureName ? `${featureName} currently unavailable` : 'Currently unavailable',
    HEADING_TEXT_FN: (featureName = 'This feature') => `${featureName} is unavailable due to scheduled maintenance`,
    BODY_TEXT_ARR: [
        'Please try again later.',
        'We apologize for the inconvenience and appreciate your patience.',
        'Thank you for using UpToDate.'
    ]
});

const SIDEBAR_TOGGLE_GITT_TEXT = 'Graphics in this topic';
const SIDEBAR_TOGGLE_OG_TEXT = 'Other graphics';
const SIDEBAR_TOGGLE_AIT_TEXT = 'Appears in topics';
export const C_GRAPHICS = Object.freeze({
    MULTI_GRAPHIC_SEPARATOR: '~',
    VIEWER_DEFAULT_SIDEBAR_TITLE: SIDEBAR_TOGGLE_GITT_TEXT,
    SIDEBAR_TOGGLE_GITT_TEXT,
    SIDEBAR_TOGGLE_OG_TEXT,
    SIDEBAR_TOGGLE_AIT_TEXT,
    SIDEBAR_ACCORDION_TITLE: 'Related Content',
    SIDEBAR_ACCORDION_AIT: Object.freeze({
        label: SIDEBAR_TOGGLE_AIT_TEXT,
        id: 'appearsInTopics'
    }),
    SIDEBAR_ACCORDION_GITT: Object.freeze({
        label: SIDEBAR_TOGGLE_GITT_TEXT,
        id: 'graphicsInTopic'
    }),
    SIDEBAR_ACCORDION_OG: Object.freeze({
        label: SIDEBAR_TOGGLE_OG_TEXT,
        id: 'otherGraphics'
    }),
    TYPE: {
        MOVIE: 'MOVIE',
        EMMI: 'emmi'
    },
    SUB_TYPE: {
        MOVIE: 'graphic_movie'
    },
    SECTION_TITLE: {
        ALGORITHM: 'Algorithms',
        DIAGNOSTICIMAGE: 'Diagnostic Images',
        FIGURE: 'Figures',
        FORM: 'Forms',
        PICTURE: 'Pictures',
        MOVIE: 'Movies',
        TABLE: 'Tables',
        WAVEFORM: 'Waveforms',
        GRAPHICALANSWER: 'Graphical Answers',
        OTHERGRAPHICS: 'Other Graphics'
    }
});

export const C_QAP = Object.freeze({
    UI: {
        QAP_TYPE: 'QAP',
        DEFAULT_MOBILE_HEIGHT: 300,
        LABI_MOBILE_HEIGHT: 500,
        MOBILE_HEIGHT_CHECK_DELAY_MS: 10
    },
    PANEL: {
        DRUG_INFO: 'drug_info',
        DRUG_INFO_PANEL: 'drug_info_panel',
        DRUG_INT: 'drug_international',
        DRUG_FORMULATION: 'drug_formulary',
        DRUG_DXY: 'drug_dxy',
        DRUG_FOREIGN: 'drug_foreign',
        DRUG_ROUTED: 'drug_routed',
        PCU: 'medical_pcu',
        LABI: 'labi',
        PATHWAYS: 'pathways',
        RX_TRANSITIONS: 'rx_transitions'
    },
    SECTION: {
        ACCORDION: 'accordion',
        ALERT: 'alert',
        LINKS: 'links',
        DRUG_INTERACTIONS: 'dip',
        RX_TRANSITIONS: 'rx_transitions'
    },
    TYPES: {
        DRUG: 'drug',
        ROUTED: 'DRUG_LANDING_PAGE',
        PATHWAY: 'ICG',
        LABI: 'narrative'
    },
    SUBTYPES: {
        LABI: 'narrative_labi',
        PCU: 'medical_pcu',
        INTERNATIONAL: 'drug_international'
    }
});

export const C_SEARCH_RESULTS = Object.freeze({
    RESULT_TYPE: {
        BROAD_QUERY_PANEL: 'BQP',
        KEY_POINTS_PANEL: 'KPP',
        GRAPHICS_PANEL: 'GP',
        GRAPHICAL_ANSWERS: 'GA',
        GRAPHIC: 'graphic',
        ICG: 'ICG',
        DRUG: 'drug',
        VISUALDX: 'visualdx',
        NARRATIVE_LABI: 'narrative_labi',
        RX_TRANSITIONS: 'rx_transitions',
        DIP: 'dip'
    },
    RESULT_SUBHIT_TYPE: {
        ICG: 'icg'
    }
});

export const C_PATHWAYS = Object.freeze({
    toc: 'table-of-contents/pathways',
    pathwayDisabledTooltipHTML: '<div class="pathway-unavailable-tooltip">This UpToDate Pathway has been disabled by your organization.</div>',
    pathwayNotEnabledTooltipHTML: '<div class="pathway-unavailable-tooltip">This Pathway is not included in your UpToDate Subscription.</div>',
    pathwayCustomizedTooltipHTML: '<div class="pathway-customized-tooltip">This Pathway has been edited by your organization and may not be consistent with UpToDate\'s recommendations.</div>'
});

export const C_TOC = Object.freeze({
    CHECK_HIDE_EXPAND_COLLAPSE_DELAY: 500,
    PATHWAYS_NAME: 'pathways',
    PATHWAYS_TOC_FILTER_NAME: 'pathways_pathwaymain',
    PATHWAYS_TOC_FILTER_SEARCH_SETTINGS_MAX: 10
});

export const FORMULARY_TYPES = Object.freeze({
    PATHWAYS: 'pathways',
    TOPIC: 'topic'
});


export const C_PAGE_TYPE = Object.freeze({
    DRUG_LANDING_PAGE: 'DRUG_LANDING_PAGE',
    FORMULATION_PAGE: 'FORMULATION_PAGE'
});

export const USER_CONTENT_FEEDBACK = Object.freeze({
    TYPE: {
        CALCULATOR: 'calculator',
        LABI: 'labi',
        DRUG: 'drug',
        PATHWAYS: 'pathway',
        TOPIC: 'topic',
        GRAPHIC: 'graphic' 
    },
    TITLE: {
        TOPIC: 'Topic Feedback',
        CALCULATOR: 'Calculator Feedback',
        GRAPHIC: 'Graphic Feedback'
    },
    LINK: {
        QUALTRICS_FEEDBACK_URL: 'https://research.uptodate.com/jfe/form/SV_3zayRu9amW74SVM',
        CONTACT_SUPPORT_URL: 'https://www.wolterskluwer.com/en/know/ce-contact-support',
        CONTACT_SUPPORT_UCC_URL: 'https://www.uptodate.cn/home/contact-us'
    }
});

export const C_MAG_MUTUAL = Object.freeze({
    MAG_MUTUAL: 'MAG_MUTUAL',
    INFO: 'Content by MagMutual, exclusively for policyholders.',
    DISCLAIMER_BOLDED: 'This is not UpToDate content,',
    DISCLAIMER: ' it was not authored or reviewed by UpToDate, and it is not subject to UpToDate\'s Editorial Policy.',
    MITIGATE_YOUR_RISK: 'Mitigate Your Liability Risk',
    RISK_REPORT_LINK: 'https://learning.magmutual.com/content-library?selectedResources=Risk+Report',
    COLLAPSED_TITLE: 'Learn How to Mitigate Your Liability Risk',
    DESCRIPTION: 'MagMutual has identified the following drivers of medical liability:'
});

/**
 * Constants for My Account.
 */
export const C_ACCOUNT = Object.freeze({
    ROUTE_URLS: {
        CONSOLIDATE_ACCOUNTS: '/account/consolidate-accounts',
        RESET_PASSWORD: '/account/reset-password?status',
        CHANGE_PASSWORD: '/account/change-password',
        ERROR: '/account/error',
        LOGIN_REGISTER: '/login-register',
    },
    SIDEBAR: [
        { title: 'My Account',
            links: [
                { name: 'Contact Information', link: '/account/contact' },
                { name: 'Subscription', link: '/account/subscription2' },
                { name: 'User Name and Password', link: '/account/change-login' },
                { name: 'Account Access Points', link: '/account/access-points' },
                { name: 'Merge Accounts', link: '/account/consolidate-accounts', id: 'consolidate', refresh: true },
                { name: 'Download Center', link: '/account/download' },
                { name: 'Manage My Devices', link: '/account/manage-devices' }
            ] }
    ],
    FORM_MESSAGING: {
        UNEXPECTED_ERROR: 'Unexpected Error',
        INVALID_LOGIN: 'INVALID_LOGIN',
        MAXIMUM_ATTEMPTS: 'MAXIMUM_ATTEMPTS',
        GENERIC_ERROR: 'GENERIC_ERROR',
        ERROR_TITLE: 'Incorrect Sign-in',
        LOGIN_ERROR_TITLE: 'Incorrect Sign In',
        USERNAME_ERROR: 'Username is required',
        PASSWORD_ERROR: 'Password is required',
        SAME_USERNAME_ERROR: 'You entered credentials for the account you are currently signed into. Please enter sign-in credentials for a different account.',
        SAME_USERNAME_ERROR_TITLE: 'Accounts must be different',
        MAX_ATTEMPTS_TITLE: 'Maximum attempts',
        MERGE_ERROR_TITLE: 'Merge error',
        FIND_ACCOUNT_ERROR_TITLE: 'Find Account Failed',
        FIND_ACCOUNT_DEFAULT_MESSAGE: 'Error occurred. Please check input data',
        MERGE_DEFAULT_TITLE: 'Cannot Merge',
        MERGE_DEFAULT_MESSAGE: 'These accounts cannot be merged.',
        CANNOT_MERGE_CONTACT: ' Please contact customer service for assistance at 1-800-998-6374 or &nbsp;+1-781-392-2000. You can also email us at <a href="mailto:customerservice@uptodate.com">customerservice@uptodate.com</a>',
        CANNOT_MERGE_UNMATCHING_LASTNAMES: 'We\'re sorry, accounts with different last names cannot be merged.',
        CANNOT_MERGE_PATIENT_ACCOUNTS: 'We\'re sorry, patient accounts cannot be merged.',
        CANNOT_MERGE_CLINICAL_CONSULTANT: 'We\'re sorry, these accounts cannot be merged.',
        CANNOT_MERGE_DIFFERENT_BIZ_UNITS: 'We\'re sorry, accounts from different business units cannot be merged.',
        MERGE_SOURCE_TARGET_SAME: '<ul class="merge-notification-list"><li>The source and the target are the same account.</li><li>The account "To Be Deactivated" and the account "Remaining" are the same.</li><li>Please enter sign-in information for a different account.</li></ul>',
        MERGE_CME_MESSAGE: 'Current Credit Total',
        MERGE_SUCCESS_TITLE: 'Success',
        MERGE_SUCCESS_MESSAGE: '<ul class="merge-notification-list"><li>You have transferred your credits, if any, and deactivated your account.</li><li>Your credits may take up to 24 hours to update.</li><li>You may also need to sign out and sign back in to your account.</li></ul>',
        MERGE_FAILURE_MESSAGE: 'We\'re sorry, unable to merge the accounts online.',
        MERGE_FAILURE_TYPE: {
            INVALID_LOGIN: 'global.login.error',
            MAXIMUM_ATTEMPTS: 'login.error.96.message',
            INVALID_SESSION: 'NO_USER_IN_SESSION',
            MERGE_RULE_VIOLATION: 'cannot.connect.account',
            CONSOLIDATE_FAILURE: 'MERGE_FAILED',
            CONSOLIDATE_MERGE_ERROR: 'Merge failed',
            CONSOLIDATE_CANNOT_CONNECT: 'cannot.connect.account.consolidate',
            MERGE_RULE_SAME_USER: 'merge.source.target.same',
            CONSOLIDATE_SAME_USER: 'merge.source.target.same.consolidate',
            UNMATCHING_LAST_NAMES: 'cannot.merge.unmatching.lastnames'
        },
        MERGE_ERROR_SETS: Object.freeze({
            INCORRECT_SIGNIN: new Set([
                'global.login.error',
                'global.login.error.wayf',
                'INVALID_CREDENTIALS',
                'USER_NOT_FOUND'
            ]),
            MAX_ATTEMPTS: new Set([
                'login.error.96.message'
            ]),
            INVALID_MERGE: new Set([
                'NO_USER_IN_SESSION',
                'cannot.connect.account',
                'cannot.merge.unmatching.lastnames',
                'You cannot connect this account.  Please register for a new account.',
                'cannot.merge.different.biz.units',
                'cannot.merge.two.individuals',
                'cannot.merge.two.active.enterprise.remote',
                'cannot.merge.unmatching.firstnames',
                'merge.not.allowed.for.patients',
                'merge.not.allowed.for.smgr.admin',
                'merge.not.allowed.for.bad.multipub.status',
                'merge.not.allowed.for.utdid1.is.child.in.merge.hierarchy',
                'merge.not.allowed.for.enterprise.account.owner',
                'merge.not.allowed.for.guests'
            ]),
            NEW_USER_MERGE: new Set([
                'cannot.merge.unregistered.user',
                'cannot.merge.unregistered.user.consolidate'
            ]),
            VALIDATE_ACCOUNT: new Set([
                'MERGE_FAILED',
                'cannot.connect.account.consolidate'
            ]),
            ERROR_STATUSES: new Set([
                'Merge failed',
                'cannot.merge.unmatching.lastnames',
                'cannot.merge.different.biz.units',
                'cannot.merge.two.individuals',
                'cannot.merge.two.active.enterprise.remote',
                'cannot.merge.unmatching.firstnames',
                'merge.not.allowed.for.patients',
                'merge.not.allowed.for.smgr.admin',
                'merge.not.allowed.for.bad.multipub.status',
                'merge.not.allowed.for.utdid1.is.child.in.merge.hierarchy',
                'merge.not.allowed.for.enterprise.account.owner',
                'merge.not.allowed.for.guests'
            ]),
            SAME_USER_ERROR_STATUSES: new Set([
                'merge.source.target.same.consolidate',
                'merge.source.target.same'
            ])
        }),
        MERGE_TYPES: {
            SSO: 'SSO',
            NON_SSO: 'NON_SSO',
            NON_SSO_AND_SSO: 'NON_SSO_AND_SSO',
            SSO_AND_SSO: 'SSO_AND_SSO',
            INDIVIDUAL: 'IND',
            SSO_WINNER: 'SSO_WINNER',
            TARGET_SSO_WINNER: 'TARGET_SSO_WINNER'
        },
        MERGE_NOT_ALLOWED: 'Account information can be changed when you purchase a full individual subscription. <a href="/store?SHORTCUT=true">Click here</a> to subscribe to UpToDate.',
        MERGE_NOT_ALLOWED_FOR_GUESTS: 'We\'re sorry, guest accounts cannot be merged.',
        ERROR_TITLE_LOGIN_REQUIRED: 'Sign-in Required',
        ERROR_MESSAGE_MY_ACCOUNT_LOGIN: 'To access My Account, please verify your identity by signing in with your UpToDate password.',
        WAYF_GENERIC_ERROR_ID: 'global.login.error.wayf',
        CONSOLIDATE_INCORRECT_SIGN_IN: 'INVALID_CREDENTIALS',
        CONSOLIDATE_USER_NOT_FOUND: 'USER_NOT_FOUND',
        GUID_MERGE_ERROR: 'You cannot connect this account.  Please register for a new account.',
        EMAIL_NOTIFICATION_TITLE: 'Entry of a different email than your enterprise email',
        EMAIL_NOTIFICATION_TITLE_REDESIGN: 'Contact Email below is different than your enterprise email',
        EMAIL_NOTIFICATION_BODY: emailAddress => `<ul class="email-notification-content"><li>It is <b>ok to use a different email</b> for your UpToDate account.</li><li>When signing in to <b>UpToDate with Single Sign-On (SSO)</b>, please use your enterprise email, <b id="emailDisclaimerMessage" title="${emailAddress}">${emailAddress}</b></li></ul>`,
        FIELDS_NEED_INFO_TITLE: 'Fields need information',
        FIELDS_NEED_INFO_BODY: '<ul class="fields-notification-content"><li>Field(s) on this form require a selection or value.</li><li>Please scroll to view fields needing information.</li></ul>',
        PRACTICE_SETTING_EMPTY_VALUE: '0',
        INCORRECT_USERNAME_PASSWORD: 'Incorrect Username and/or Password.'
    },
    MESSAGE_TYPE: {
        SUCCESS: 'success',
        ERROR: 'error',
        INFO: 'info'
    }
});

export const C_LANGUAGES_NAMES = Object.freeze({
    IN: { NAME: 'BAHASA_INDONESIAN', CODE: 'in' },
    ZH_HANS: { NAME: 'SIMPLIFIED_CHINESE', CODE: 'zh-Hans' },
    ZH_HANT: { NAME: 'TRADITIONAL_CHINESE', CODE: 'zh-Hant', COUNTRY_CODE: 'zh-TW' },
    DE: { NAME: 'GERMAN', CODE: 'de' },
    EN: { NAME: 'ENGLISH', CODE: 'en', COUNTRY_CODE: 'en-US' },
    ES: { NAME: 'SPANISH', CODE: 'es', COUNTRY_CODE: 'es-419' },
    FR: { NAME: 'FRENCH', CODE: 'fr' },
    IT: { NAME: 'ITALIAN', CODE: 'it' },
    JA: { NAME: 'JAPANESE', CODE: 'ja' },
    KO: { NAME: 'KOREAN', CODE: 'ko' },
    NL: { NAME: 'DUTCH', CODE: 'nl' },
    PL: { NAME: 'POLISH', CODE: 'pl' },
    PT: { NAME: 'PORTUGESE', CODE: 'pt', COUNTRY_CODE: 'pt-br' },
    RU: { NAME: 'RUSSIAN', CODE: 'ru' },
    TR: { NAME: 'TURKISH', CODE: 'tr' },
    VI: { NAME: 'VIETNAMESE', CODE: 'vi' },
    AR: { NAME: 'ARABIC', CODE: 'ar' },
    EL: { NAME: 'GREEK', CODE: 'el' },
    HT: { NAME: 'HAITIAN_CREOLE', CODE: 'ht' },
    PA: { NAME: 'PUNJABI', CODE: 'pa' },
    TL: { NAME: 'TAGALOG', CODE: 'tl' }
});

export const C_LANGUAGES_LIST = Object.freeze([
    { name: C_LANGUAGES_NAMES.IN.NAME, value: C_LANGUAGES_NAMES.IN.CODE },
    { name: C_LANGUAGES_NAMES.ZH_HANS.NAME, value: C_LANGUAGES_NAMES.ZH_HANS.CODE },
    { name: C_LANGUAGES_NAMES.ZH_HANT.NAME, value: C_LANGUAGES_NAMES.ZH_HANT.CODE },
    { name: C_LANGUAGES_NAMES.DE.NAME, value: C_LANGUAGES_NAMES.DE.CODE, marketing: true },
    { name: C_LANGUAGES_NAMES.EN.NAME, value: C_LANGUAGES_NAMES.EN.CODE, marketing: true },
    { name: C_LANGUAGES_NAMES.ES.NAME, value: C_LANGUAGES_NAMES.ES.CODE, marketing: true },
    { name: C_LANGUAGES_NAMES.FR.NAME, value: C_LANGUAGES_NAMES.FR.CODE },
    { name: C_LANGUAGES_NAMES.IT.NAME, value: C_LANGUAGES_NAMES.IT.CODE },
    { name: C_LANGUAGES_NAMES.JA.NAME, value: C_LANGUAGES_NAMES.JA.CODE, marketing: true },
    { name: C_LANGUAGES_NAMES.KO.NAME, value: C_LANGUAGES_NAMES.KO.CODE },
    { name: C_LANGUAGES_NAMES.NL.NAME, value: C_LANGUAGES_NAMES.NL.CODE },
    { name: C_LANGUAGES_NAMES.PL.NAME, value: C_LANGUAGES_NAMES.PL.CODE },
    { name: C_LANGUAGES_NAMES.PT.NAME, value: C_LANGUAGES_NAMES.PT.CODE, marketing: true },
    { name: C_LANGUAGES_NAMES.RU.NAME, value: C_LANGUAGES_NAMES.RU.CODE },
    { name: C_LANGUAGES_NAMES.TR.NAME, value: C_LANGUAGES_NAMES.TR.CODE },
    { name: C_LANGUAGES_NAMES.VI.NAME, value: C_LANGUAGES_NAMES.VI.CODE }
]);

export const C_SMGR_ERRORS = Object.freeze({
    invalidGuid: 'ERROR.SYSTEM_ERROR',
    systemError: 'ERROR.SYSTEM_ERROR',
    expiredGuid: 'MYACCOUNT.ACCOUNT_GUID_EXPIRED',
    consumedGuid: 'MYACCOUNT.ACCOUNT_GUID_CONSUMED',
    accountExpired: 'MYACCOUNT.ACCOUNT_EXPIRED',
    disabledUser: 'MYACCOUNT.ACCOUNT_DISABLED'
});

const uccUrl = 'http://www.uptodate.cn/home/';
export const C_FOOTER_CORE = Object.freeze({
    help: {
        url: '/home/help',
        title: 'Help',
        uccUrl: `${uccUrl}help`
    },
    aboutUs: {
        url: WK_UPTODATE_PREFIX,
        title: 'About Us',
        uccUrl: `${uccUrl}about-us`
    },
    contactUs: {
        url: `${WK_UPTODATE_PREFIX}/contact`,
        title: 'Contact Us',
        uccUrl: `${uccUrl}contact-us`
    },
    sla: {
        url: '',
        title: 'Terms of Use'
    },
    policies: {
        url: `${WK_UPTODATE_PREFIX}/policies-legal`,
        title: 'Policies',
        uccUrl: `${uccUrl}policies`
    },
    supportTag: {
        url: '',
        title: 'Support Tag'
    },
    uptodateNews: {
        url: `${WK_UPTODATE_PREFIX}/about/expert-insights`,
        title: 'UpToDate News'
    },
    accessOptions: {
        url: `${WK_UPTODATE_PREFIX}/about/mobile-apps/uptodate`,
        title: 'Mobile Access',
        uccUrl: `${uccUrl}mobile-apps-and-mobile-web`
    },
    trainingCenter: {
        url: `${WK_UPTODATE_PREFIX}/resources`,
        title: 'Help & Training'
    },
    demos: {
        url: `${WK_UPTODATE_PREFIX}/resources/user-academy/training-videos`,
        title: 'Demos'
    },
    editorialPolicy: {
        url: `${WK_UPTODATE_PREFIX}/policies-legal/editorial-policy`,
        uccUrl: `${uccUrl}policies`
    },
    testimonials: {
        url: `${WK_UPTODATE_PREFIX}/about/customer-success-stories`,
        uccUrl: `${uccUrl}customer-testimonials`
    },
    woltersKluwer: {
        url: 'http://wolterskluwer.com/'
    },
    careers: {
        url: 'http://careers.wolterskluwer.com/'
    },
    helpTraining: {
        url: `${WK_UPTODATE_PREFIX}/resources`,
        uccUrl: `${uccUrl}help`
    },
    citingOurContent: {
        url: `${WK_UPTODATE_PREFIX}/policies-legal/linking-policy`,
        uccUrl: `${uccUrl}citing-an-uptodate-topic`
    },
    whatsNew: {
        url: '/contents/table-of-contents/whats-new'
    },
    aiTrustedContent: {
        url: `${WK_UPTODATE_PREFIX}/ai-clinical-decision-support`
    },
    pressAnnouncements: {
        url: 'https://www.wolterskluwer.com/en/news?f:contenttype=News%20Page%7CPress%20Release%20Page&f:solutionsuite=%7BCC75F61E-45E7-4268-87BE-7A43E934C228%7D%7C%7B3AEA1486-C0C5-417E-B995-F63014BF5748%7D%7C%7B53FBCDA3-3933-4550-8B35-A726EC051382%7D%7C%7B4699E27F-6BEC-4512-851C-791E9CB8E8F0%7D%7C%7B490C6039-F9B5-48EB-ADED-3F87733B6C6A%7D',
        uccUrl: `${uccUrl}press-release`
    },
    intheNews: {
        url: '/home/uptodate-news',
        uccUrl: `${uccUrl}uptodate-in-media`
    },
    webinars: {
        url: `${WK_UPTODATE_PREFIX}/resources/webinars`
    },
    uptodateLogin: {
        url: '/login'
    },
    cme: {
        url: `${WK_UPTODATE_PREFIX}/about/continuing-medical-education`
    },
    mobileApps: {
        url: `${WK_UPTODATE_PREFIX}/about/mobile-apps/uptodate`,
        uccUrl: `${uccUrl}mobile-apps-and-mobile-web`
    },
    EHRIntegration: {
        url: `${WK_UPTODATE_PREFIX}/about/ehr-integration`
    },
    healthIndustryPodcasts: {
        url: '/home/uptodate-talk-podcasts'
    },
    signUp: {
        url: 'https://go.wolterskluwer.com/utd_ind_cms_contact_info.html',
        uccUrl: 'https://uptodatechina.secure.force.com/IndividualSubscribers?SourceCode=MarketingSiteSignUp'
    },
    privacyPolicy: {
        url: `${WK_UPTODATE_PREFIX}/policies-legal/privacy-policy`,
        uccUrl: `${uccUrl}uptodate-china-privacy-policy`
    },
    trademarks: {
        url: `${WK_UPTODATE_PREFIX}/policies-legal/copyright-trademarks-logos`
    },
    subscriptionLicense: {
        url: 'https://www.wolterskluwer.com/en/know/clinical-effectiveness-terms',
        target: '_blank',
        rel: 'noopener noreferrer',
        uccUrl: '/legal/china/license',
        uccTarget: '',
        uccRel: ''
    }
});

/**
 * Event names
 */
export const C_EVENTS = Object.freeze({
    TRACK_CLICK_EVENT: 'wkutd.trackClickEvent',
    TRACK_UI_CLICK_EVENT: 'wkutd.trackUiClickEvent',
    OPEN_MODAL: 'wkutd.open-myuptodate-modal',
    START_POSITION_FOOTER: 'wkutd.startPositionFooter',
    OPEN_SUPPORT_TAG_MODAL: 'wkutd.open-support-tag-modal',
    POSITION_FOOTER: 'wkutd.position-footer',
    HOVER_TOOLTIP_CLICK: 'wkutd.hover-tooltip-click',
    SET_CREDITS_TO_REDEEM: 'wkutd.set-credits-to-redeem',
    OPEN_CME_DETAILS_MODAL: 'wkutd.open-cme-details-modal',
    OPEN_CONTACT_INFO_MODAL: 'wkutd.open-contact-info-modal',
    MOC_BOARD_REMOVED: 'MOC_BOARD_REMOVED',
    TRANSLATE_FEATURED_SEARCH_RESULT: 'wkutd.translate-featured-result',
    MOC_USER_INITIALISED: 'wkutd.moc-user-initialised',
    OPEN_MYUPTODATE_MODAL: 'wkutd.open-myuptodate-modal',
    ASSIGNED_OPTIMIZELY_FEATURES: 'wkutd.assigned-optimizely-features',
    APP_DATA_LOADED: 'wkutd.data-loaded',
    SLA_MESSAGE_EVENT: 'wkutd.sla-message-event',
    NEXT_STEPS_MERGE_CHECK: 'wkutd.checkNextStepsMergeStatus',
    REFRESH_TOC: 'wkutd.refreshToc',
    CLOSE_TRANSLATION_WARNING: 'wkutd.close-translation-warning-modal',
    OPEN_TRANSLATION_WARNING: 'wkutd.open-translation-warning-modal',
    OPEN_TRANSLATION_FEEDBACK_INSTRUCTION: 'wkutd.open-translation-feedback-instruction-modal',
    REMOUNT_TOPIC_VIEW: 'wkutd.remount-topic-view',
    OPEN_LEARN_MORE: 'wkutd.open-learn-more-modal',
    AI_SEARCH_DEADLINE: 'wkutd.ai-search-deadline',
    SHOW_POST_REG_MODAL: 'wkutd.show-post-reg-modal'
});

export const C_CSRF = Object.freeze({
    COOKIES_TOKEN: 'UTD-X-CSRF-TOKEN',
    HEADER_TOKEN: 'ANTI-CSRF-UTD-TOKEN'
});

export const C_ACTIVE_TRACK_TYPES = Object.freeze({
    SEARCH: 'SEARCH',
    TIME: 'TIME'
});

export const C_MONTH_NAMES = Object.freeze(
    [ 'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December' ]
);

export const C_TABS = Object.freeze({
    WIDTH_BOUNDS_RANGE_PX: 8,
    INITIAL_TAB_RECHECK_DELAY_MS: 200,
    RESIZE_RECHECK_THROTTLEBOUNCE_DELAY_MS: 200
});

export const C_MY_ACCOUNT = Object.freeze({
    MAX_DEVICES_ALLOWED: 2,
    MAX_DEVICES_ALLOWED_UCC: 1,
    MIN_USERNAME_LENGTH: 6,
    MAX_USERNAME_LENGTH: 50,
    UPDATE_LOGIN_SUCCESS: 'SUCCESS',
    UPDATE_LOGIN_PENDING: 'PENDING',
    OLD_PASSWORD_INVALID_ERROR: 'OLD_PASSWORD_INVALID_ERROR',
    NEW_USERNAME_ERROR: 'NEW_USERNAME_ERROR',
    USER_NAME_IN_USE_ERROR: 'USER_NAME_IN_USE_ERROR',
    NEW_PASSWORD_ERROR: 'NEW_PASSWORD_ERROR',
    NEW_PASSWORD_USER_NAME_ERROR: 'NEW_PASSWORD_USER_NAME_ERROR',
    NEW_PASSWORD_SECURITY_ERROR: 'NEW_PASSWORD_SECURITY_ERROR',
    DESKTOP_DEVICE: 'desktop',
    MOBILE_DEVICE: 'mobile',
    ACCESS_LEVELS: {
        UNREGISTERED: 'UNREGISTERED',
        NOT_LOGGED_IN: 'NOT_LOGGED_IN',
        GUEST_ACCESS: 'GUEST_ACCESS',
        NO_MY_ACCOUNT_FEATURE: 'NO_MY_ACCOUNT_FEATURE',
        INCOMPLETE_MY_ACCOUNT_FEATURE: 'INCOMPLETE_MY_ACCOUNT_FEATURE',
        FULL_ACCESS: 'FULL_ACCESS'
    },
    PHONE_TYPES: [ 'work', 'home', 'cell' ],
    SIDEBAR_REQUEST: {
        PENDING: 'PENDING',
        APPROVED: 'APPROVED',
        SHOWN: 'SHOWN'
    },
    FORM_NAME: 'MY_ACCOUNT',
    MY_ACCOUNT_QUERY_PARAM: 'myAccount',
    MISSING_PARAMETER: 'MISSING_PARAMETER',
    CONTACT_INFO_LABEL: 'Link to contact information dialog'
});

export const C_LOGIN_WORKFLOW = Object.freeze({
    PROMPT_USER_FOR_PASSWORD: 1,
    REDIRECT_TO_SSO: 2,
    SHOW_NEXT_STEPS_FOR_SIGN_IN: 3
});

export const C_LOCALIZATION = Object.freeze({
    LOCAL_LANGUAGE_ID: 0,
    ENGLISH_ID: 1,
    ENGLISH_CODE: C_LANGUAGES_NAMES.EN.COUNTRY_CODE,
    LANGUAGE_PARAM_POSITION: 2,
    SOURCE: 'machinetranslate',
    PP_SOURCE: {
        KPP: 'KPP',
        BQP: 'BQP',
        TOPIC: 'TOPIC'
    },
    TRANSLATION_FEEDBACK_INSTRUCTION: {
        TITLE: 'Provide translation feedback',
        HEADING: 'Help UpToDate with your feedback',
        BODY_TEXT_ARR: [
            'To provide feedback, please select the sentence(s) you have feedback for.',
            'A 翻訳に関するフィードバックを送る button will appear.',
            'Select that button to help UpToDate make the translation better.'
        ]
    }
});

export const C_TABLE_SIZE_CLASS = {
    default: '',
    small: 'wk-table-small',
    extraSmall: 'wk-table-extra-small'
};

export const C_KEY_POINTS = Object.freeze({
    CONTRIBUTORS_TEXT: 'Key Points Panels are created and maintained by our Deputy Editors in conjunction with the authors and section editors of the underlying topics. They provide select key points from the underlying topics, and the underlying evidence is explicitly available in the linked topics.'
});

/**
 * @type {string} sso selection cookies name
 * Remember sso institution selection and put value to cookies.
 */
export const SSO_INSTITUTION_SELECTION = 'SSO_INSTITUTION_SELECTION';

/**
 * This regular expression outlines our rules for a syntactically correct email address.
 * Please check with Business Systems Team and consider other downstream parts of the company before changing.
 *The first part of the regex accepts only letters, numbers, underscores, hyphens, plus-signs, periods in the username.
 *The second part allows for an unlimited number of subdomains. Only letters, numbers, and hyphens are accepted.
 *The third part ensures that the top-level domain has between 2 and 24 characters, inclusive.
 * Only letters, numbers, and hyphens are accepted in the top-level domain.
 */
// eslint-disable-next-line no-useless-escape
export const emailRegex = /^([\w_\-\.\+]+)@(?:[a-zA-Z0-9\-]+\.)+[a-zA-Z0-9\-]{2,24}$/;
/* eslint-disable max-len */
export const xssInjectionPattern = /<\/?[^>]*(img|script|href|on[^>\s]+|src|iframe|input|background|dynsrc|lowsrc|style|size|meta|url)[^>]*>/ig;

export const numericRegex = /^[0-9]*$/;

export const isOidcRegex = /.*(OI|SO)\(.*/;
export const isIndividualRegex = /^.*IND.*$/;

export const byolErrorRegex = /^.*\.byol/;

/**
 * The regular expressions below outline our rules for the special characters that a password can and cannot have.
 * Only the following special characters are allowed:
 *       . - _ @ # $ * ! ( ) + =
 */
// eslint-disable-next-line no-useless-escape
export const passwordSpecialCharactersRegex = /[.\-_@#\$*!()+=]/;

export const validUserNamePwdCharactersRegex = /^[A-Za-z0-9.\-_@#$*!()+=]*$/;

export const validDomesticPhoneRegex = /^1?\s*-?\s*(\d{3}|\(\s*\d{3}\s*\))\s*-?\.?\/?\s*\d{3}\s*-?\.?\/?\s*\d{4}$/;

export const validInternationalPhoneRegex = /^(\d|-|\+| |\.|){6,15}$/;

export const pathwaysUrlRegex = /^\/?pathway\/\d+$/i;
export const rxTransitionsUrlRegex = /^\/?rxtransitions$/i;
export const drugInteractionsUrlRegex = /^\/?drug-interactions\/?$/i;

export const numberWithSideSpacesPattern = '^\\s*\\d+\\s*';

export const dateTimeDayRegex = /^\d\d?$/;
export const dateTimeMonthRegex = /^\d\d?$/;
export const dateTimeYearRegex = /^(\d){4}$/;
export const validMailingAddressRegex = /^[-.#',/ A-Za-z0-9]*$/;
export const validZipCodeRegex = /^[- A-Za-z0-9]*$/;
export const validPhoneNumberRegex = /^[-+() 0-9]+$/;

export const externalImages = Object.freeze({
    UTD_LOGO: '/images/UTD3_masthead.png'
});

export const utdCcLogoAlternateText = 'UpToDate\u4E34\u5E8A\u987E\u95EE';
export const invisibleCharacter = '\ufeff';

export const millisecondsToDaysMultiplier = 1 / 1000 / 60 / 60 / 24;

export const maxEmailLength = 50;
export const maxSearchInputLength = 70;
export const maxContactFieldLength = 30;
export const maxPasswordFieldLength = 24;

export const boardAuthorityCodeAbts = 'ABTS';

export const dateOfBirthMinDate = new Date(1900, 0, 1);

export const footerMinDistanceScaler = 0.14;

export const MONTH_NAMES = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul',
    'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];

export const UTD_REGISTER_BENEFITS_ARRAY = [
    'Complete your account registration and benefit from more convenient access to our trusted clinical content.',
    'Plus, earn and redeem "Continuing Medical Education" (CME) credits, if applicable, while you work.'
];

export const UTD_GUEST_PASS_REGISTRATION_MESSAGES = [
    'By registering, you are agreeing to receive periodic emails with important news and product updates from UpToDate.', 
    'You can opt out of these communications at any time.',
    'View our privacy policy for more details.'
];

export const REG_MESSAGE_DEFAULT =
    'Make the most of your UpToDate experience: Register for an account and benefit from mobile access to our trusted clinical content. Plus, earn and redeem CME/CE/CPD credits while you work.';
  
export const REG_MESSAGE_SSO =
    'Make the most of your UpToDate experience: Complete your account registration and benefit from mobile access to our trusted clinical content. Plus, earn and redeem CME/CE/CPD credits while you work.';

export const REG_MESSAGE_NO_CME =
    'Make the most of your UpToDate experience: Complete your account registration and benefit from more convenient access to our trusted clinical content.';

export const DEFAULT_COBRANDING_MESSAGES = [ REG_MESSAGE_DEFAULT, REG_MESSAGE_SSO, REG_MESSAGE_NO_CME, '' ];

export const UTD_PASSWORD_RULES = [
    { text: 'Cannot match username' },
    { text: '8 to 24 characters' },
    { text: 'At least 1 uppercase letter' },
    { 
        text: 'At least 1 number, or special character from the following set:', 
        subText: [
            { text: '@', name: 'At Symbol' },
            { text: '#', name: 'Hash' },
            { text: '$', name: 'Dollar Sign' },
            { text: '*', name: 'Asterisk' },
            { text: '!', name: 'Exclamation Mark' },
            { text: '(', name: 'Left Parenthesis' },
            { text: ')', name: 'Right Parenthesis' },
            { text: '-', name: 'Hyphen' },
            { text: '_', name: 'Underscore' },
            { text: '+', name: 'Plus Sign' },
            { text: '=', name: 'Equals Sign' },
            { text: '.', name: 'Period' }
        ]
    }
];

export const UTD_TWO_FACTOR_AUTH_MESSAGES = [
    'Check the email account you submitted above for a message containing a verification code.',
    'If you don\'t see the email, please check your spam folder.',
    '<b>Paste or type the code below</b> in the "Verification Code" field and <b>select the "Submit Verification Code" button.</b>',
    'No account data will be saved until the code is accepted.'
];

export const UTD_TWO_FACTOR_AUTH_ERROR_MESSAGES = [
    'Verification code was not sent.',
    'Please correct errors and click on the "Verification Code" button again.'
];

export const UTD_WAYF_EMAIL_CHANGED_NOTIFICATION = [
    'It is ok to use a different email for your UpToDate account "Contact Email" field.',
    'When signing in to UpToDate with Single Sign-On (SSO), please use your enterprise email, {disclaimerEmail}'
];

export const OIDC_WELCOME_MESSAGES = Object.freeze({
    SUBSCRIBE: [ '<span class=\'ds1-utd-weight-600\'>If you have access to UpToDate</span> through your institution, but don\'t have an account yet, please register through your institution and then sign in.',
        '<span class=\'ds1-utd-weight-600\'>If you don\'t have access to UpToDate yet</span>, please subscribe below and then sign in. You will need to sign in only once. '
    ],
    REGISTER: [ '<span class=\'ds1-utd-weight-600\'>If you have access to UpToDate</span> through your institution, but don\'t have an account yet, please register.',
        '<span class=\'ds1-utd-weight-600\'>If you are already registered</span>, please sign in.'
    ]
});

export const REGISTRATION_DEFAULT_ERROR_MESSAGE = [
    'There was an error during submission.',
    'Please try again.',
    'If the problem persists, contact ' 
        + '<a href="/home/contact-us" target="_blank">customer support</a>'
];

export const REGISTRATION_FIELD_ERROR_MESSAGE = [
    'Please review the form, correct any errors, and submit again.'
];

const MAX_ATTEMPTS_MESSAGE = 'You have reached the maximum number of attempts for entering a verification code.';
export const REGISTRATION_NO_OTC_ENTRY_ERROR_MESSAGE = [
    MAX_ATTEMPTS_MESSAGE,
    'Please select "Send New Code" and enter the new Verification Code.'
];

export const REGISTRATION_OTC_EXIT_BROWSER_ERROR_MESSAGE = [
    MAX_ATTEMPTS_MESSAGE,
    'Please exit your browser and try again.'
];

export const C_REG_MERGE = Object.freeze({
    GUEST_PASS_USER_FORM: 'GUEST_SELF_REGISTER',
    UNEXPECTED_ERROR: {
        message: REGISTRATION_DEFAULT_ERROR_MESSAGE.join(' '),
        heading: 'Unexpected Error'
    },
    REG_MERGE_SUCCESS_PARAM: 'regMergeSuccess',
    OIDC_MERGE_SUCCESS: 'SUCCESS_OIDC',
    REG_MERGE_SOURCE: 'mergeSource',
    REGISTRATION_MERGE_TYPE: 'registration',
    CONSOLIDATE_MERGE_TYPE: 'consolidate',
    MESSAGES: {
        REG: {
            name: 'REG',
            message: 'You\'re all set. Your next time accessing UpToDate you can sign in with this new account.',
            heading: 'Your account was registered.'
        },
        BYOL_REG: {
            name: 'BYOL_REG',
            message: '<ul class=\'ds1-utd-weight-600\'><li class=\'ds1-mb-1\'>The UpToDate link will now sign you in automatically.</li>'
                + '<li class=\'ds1-mb-1\'>You can also use your registered username and password to '
                + 'access UpToDate from anywhere.</li></ul>',
            heading: 'Your account was registered.'
        },
        BYOL_MERGE: {
            name: 'BYOL_MERGE',
            message: 'The UpToDate link will now sign you in automatically.',
            heading: 'You\'re all set.'
        },
        SSO_MERGE: {
            name: 'SSO_MERGE',
            message: '<b>There may be a delay</b> in updating your CME credits. To view these sooner, sign out and sign back in to UpToDate.',
            heading: 'Your account now has Single Sign-On (SSO).'
        },
        GENERIC_MERGE: {
            name: 'GENERIC_MERGE',
            message: '<b>There may be a delay</b> in updating your CME credits. To view these sooner, sign out and sign back in to UpToDate.',
            heading: 'Your accounts were merged successfully into one account.'
        }
    }
});

export const SALUTATION_OPTIONS = [
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Miss.', label: 'Miss.' },
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'None.', label: 'None.' },
    { value: 'Prof.', label: 'Prof.' },
    { value: 'Prof.Dr.', label: 'Prof.Dr.' }
];

export const ZIPCODE_FETCH_SUCCESS = 0;
export const REGISTRATION_REVIEW_FORM_MESSAGE = 'There was an error during submission. Please review the form, then resubmit.';
export const GUEST_PASS_REGISTRATION_DISCLAIMER = 'We respect your privacy and will not mail or share your information with any third party. This information will be used only to provide subscription services to you.';
export const GUEST_PASS_LICENSE = 'UpToDate Guest Pass';
export const REGISTRATION_FIELD_ERROR = {
    FIRSTNAME: 'Please enter a valid first name',
    LASTNAME: 'Please enter a valid last name',
    ADDRESS1: 'Please enter a valid address',
    PHONE1: 'Please enter a valid phone number',
    EMAIL: 'Please enter a valid email address',
    ZIP: 'Please enter a valid zip code',
    CITY: 'Please enter a valid city',
    VERIFYPASSWORD: 'Please ensure your passwords match',
    VERIFICATIONCODE: 'Please enter a valid verification code',
    INVALID_CODE: 'The verification code you entered is invalid. Please try again.',
    INCORRECT_EMAIL: 'The email address you entered is not valid. You must request a new code.'
};

export const REG_FIELD = Object.freeze({
    FIRST_NAME: 'firstName',
    LAST_NAME: 'lastName',
    ADDRESS1: 'address1',
    ADDRESS2: 'address2',
    EMAIL: 'email',
    PHONE1: 'phone1',
    ZIP: 'zip',
    CITY: 'city',
    USER_NAME: 'registrationUserName',
    PASSWORD: 'password',
    VERIFY_PASSWORD: 'verifyPassword',
    SALUTATION: 'salutation',
    COUNTRY: 'country',
    STATE: 'state',
    PRACTICE_SETTING: 'practiceSetting',
    PRACTICE_TYPE: 'practiceType',
    SPECIALTY: 'specialty',
    VERIFICATION_CODE: 'verificationCode',
    TERMS: 'termsAccepted'
});
export const GDPR_COOKIE_BANNER_MESSAGE = 'This site uses cookies. By continuing to browse this site, you are agreeing to our use of cookies.';

export const CONSOLIDATE_ACCOUNTS_INFO = [
    '<span class="ds1-utd-weight-bold">Merging accounts will transfer</span> your Continuing Education Credits <span class="ds1-utd-weight-bold">(CMEs)</span> into one account, if applicable.',
    'After the merge <span class="ds1-utd-weight-bold">you will have one active account</span> with all of your information.',
    '<span class="ds1-utd-weight-bold">Your next step</span> will be to identify your other account and take a quick look at your account information.'
];

export const C_PRINT = Object.freeze({
    INLINE_GRAPHICS_LABEL_SUFFIX: {
        [C_LANGUAGES_NAMES.EN.COUNTRY_CODE]: 'larger image below',
        [C_LANGUAGES_NAMES.AR.CODE]: 'تتوفَّر صورة أكبر حجمًا أدناه',
        [C_LANGUAGES_NAMES.ZH_HANS.CODE]: '大图见下',
        [C_LANGUAGES_NAMES.ZH_HANT.COUNTRY_CODE]: '下方有放大的影像',
        [C_LANGUAGES_NAMES.FR.CODE]: 'image agrandie ci-dessous',
        [C_LANGUAGES_NAMES.DE.CODE]: 'Größere Abbildung unten',
        [C_LANGUAGES_NAMES.EL.CODE]: 'μεγαλύτερη εικόνα παρακάτω',
        [C_LANGUAGES_NAMES.HT.CODE]: 'pi gwo imaj ki anba a',
        [C_LANGUAGES_NAMES.IT.CODE]: 'immagine ingrandita a piè di pagina',
        [C_LANGUAGES_NAMES.JA.CODE]: '拡大画像は以下をご覧ください',
        [C_LANGUAGES_NAMES.KO.CODE]: '아래의 더 큰 이미지',
        [C_LANGUAGES_NAMES.PL.CODE]: 'większe zdjęcie poniżej',
        [C_LANGUAGES_NAMES.PT.COUNTRY_CODE]: 'imagem maior abaixo',
        [C_LANGUAGES_NAMES.PA.CODE]: 'ਵੱਡਾ ਚਿੱਤਰ ਹੇਠਾਂ ਹੈ',
        [C_LANGUAGES_NAMES.RU.CODE]: 'Увеличенное изображение см. ниже.',
        [C_LANGUAGES_NAMES.ES.COUNTRY_CODE]: 'imagen más grande debajo',
        [C_LANGUAGES_NAMES.TL.CODE]: 'mas malaking imahe sa ibaba',
        [C_LANGUAGES_NAMES.TR.CODE]: 'görüntünün büyütülmüş hali aşağıdadır',
        [C_LANGUAGES_NAMES.VI.CODE]: 'hình ảnh lớn hơn bên dưới'
    }
});

export const C_PRIORITY_LOGIN = Object.freeze({
    LOGIN_PRIORITY_CACHE_KEY: 'loginPriority',
    UN_PW_PRIORITY: 1,
    SSO_PRIORITY: 2
});